import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Paper, Grid, Button, Select, MenuItem, FormControl, InputLabel, Tooltip } from "@mui/material";
import { Utility } from "../../../../utils/Utility";
import { DeviceDetailInfoGridProps } from "./DeviceDetailInfoGrid";
import { DeviceUiConverter } from "../../../../converter/DeviceUiConverter";
import SimTypeBox from "../../box/SimTypeBox";
import { subMonths, startOfMonth, format } from "date-fns";
import { useFormContext } from "react-hook-form";
import { DeviceDetailRequest } from "../../../terminal/api/TerminalForm";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ByteFormatter from "../../../../utils/formatter/ByteFormatter";

// テキストが省略されているか判定してツールチップを表示するコンポーネント
const EllipsisTooltip: React.FC<{ value: string; color?: string; isBold?: boolean }> = ({ value, color, isBold = false }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [value]);

  const baseStyle = {
    fontSize: "0.75rem",
    fontWeight: isBold ? 700 : 400,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: color || "#333",
    flex: 1,
    display: 'block',
    borderBottom: 'none',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <Tooltip title={isOverflowed ? value : ''} arrow>
      <Typography ref={textRef} sx={baseStyle}>
        {value}
      </Typography>
    </Tooltip>
  );
};

// テーブルセル用：テキストが省略されているか判定してツールチップを表示するコンポーネント
const TableCellTooltip: React.FC<{ value: string }> = ({ value }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        setIsOverflowed(element.scrollWidth > element.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [value]);

  return (
    <Tooltip title={isOverflowed ? value : ''} arrow>
      <span ref={textRef} style={{ 
        display: 'block', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap',
        fontSize: "0.75rem",
        fontWeight: 400,
        fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
        color: "#333",
      }}>
        {value}
      </span>
    </Tooltip>
  );
};

const style = {
  sectionBox: {
    // width: { xs: "670px", lg: "770px" },
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingBottom: "16px",
    paddingTop: "6px",
    marginTop: "-10px",
    marginBottom: "10px",
    display: "flex",
  },
  contentArea: {
    flex: 1,
    paddingLeft: "0px",
    minWidth: 0,
  },
  categoryTitle: {
    fontSize: "1.125rem",
    fontWeight: 600,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    marginBottom: "8px",
  },
  subCategoryTitle: {
    backgroundColor: '#e7f2ff',
    px: 1,
    paddingTop: 1,
    paddingBottom: '-1px',
    minHeight: '28px', maxHeight: '28px', height: '28px',
    fontWeight: 550,
    fontSize: "0.875rem",
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    marginBottom: "8px",
  },
  itemBox: {
    paddingTop: "4px",
    paddingBottom: "4px",
    borderBottom: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    gap: '1em',
    minHeight: '28px',
    maxHeight: '28px',
    overflow: 'hidden',
    lineHeight: '20px',
  },
  itemLabel: {
    width: "135px",
    fontSize: "0.8rem",
    fontWeight: 500,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#666",
    flexShrink: 0,
    display: 'inline-block',
    padding: "2px 8px",
    borderBottom: 'none',
    justifyContent: 'center',
  },
  itemValue: {
    fontSize: "0.8rem",
    fontWeight: 500,
    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
    color: "#333",
    flex: 1,
    paddingLeft: "0px",
    display: 'inline-block',
    borderBottom: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& > div': {
      height: '20px',
      display: 'flex',
      alignItems: 'center',
    },
    '& img': {
      maxHeight: '20px',
    },
    justifyContent: 'center',
  },
};

const DeviceDetailInfoGridForFS010M: React.FC<DeviceDetailInfoGridProps> = ({
  contentColor = "#16191f",
  contentFontSize = "0.95rem",
  device,
}) => {
  const navigate = useNavigate();
  const {
    getValues,
    register,
  } = useFormContext<DeviceDetailRequest>();

  const now = new Date();
  const simReplaceLogOptions = Array.from({ length: 12 }, (_, index) => ({
    key: subMonths(startOfMonth(now), index).toISOString(),
    value: <span style={{ fontSize: "0.8rem" }}>{format(subMonths(startOfMonth(now), index), "yyyy年MM月")}</span>,
  }));

  // 確認済みフラグ: modelNameが存在する場合は登録確認済み
  const isDeviceRegistered = !!(device.modelName);

  return (
    <Box sx={{ width: "100%", minHeight: '28px', maxHeight: '28px' }}>
      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* 基本情報 */}
        <Grid item lg={6}>
          <Paper sx={style.sectionBox} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>基本情報</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={{ ...style.subCategoryTitle}}>端末情報</Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                    <Typography sx={style.itemLabel}>シリアルナンバー</Typography>
                    <EllipsisTooltip value={device.devsn || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>IMEI</Typography>
                    <EllipsisTooltip value={device.sim1?.imei || '-'} />
                  </Box>
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>機種</Typography>
                    <EllipsisTooltip value={device.modelName || '-'} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>SW Ver</Typography>
                    <EllipsisTooltip value={device.softver || '-'} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>HW Ver</Typography>
                    <EllipsisTooltip value={device.hardver || '-'} />
                  </Box>
                  )}
                  {isDeviceRegistered && (
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>通信モジュール Ver</Typography>
                    <EllipsisTooltip value={device.sim1?.moduleVersion || '-'} />
                  </Box>
                  )}
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>登録日時</Typography>
                    <EllipsisTooltip value={(device.createDate ? Utility.dateFormatYmdHis(device.createDate) : '-') || '-'} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={style.subCategoryTitle}>MDM管理情報</Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                    <Typography sx={style.itemLabel}>MDM管理</Typography>
                    <EllipsisTooltip value={isDeviceRegistered ? (device.isMdmManaged ? "有効" : "無効") : "登録確認未実施"} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>テナント</Typography>
                    <EllipsisTooltip value={device.tenantName || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>端末利用者</Typography>
                    <EllipsisTooltip
                      value={device.username && device.username.trim() !== '' ? device.username : '未設定'}
                      color={!device.username ? '#bbb' : '#333'}
                    />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>タグ</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.tag && device.tag.length > 0 ? device.tag.map((t) => t.tagName).join(", ") : '未設定'}
                        color={device.tag && device.tag.length > 0 ? '#333' : '#bbb'}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>ラベル</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.label && device.label.trim() !== '' ? device.label : '未設定'}
                        color={!device.label ? '#bbb' : '#333'}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>コメント</Typography>
                    <Box sx={{ maxWidth: 240, flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.comment && device.comment.trim() !== '' ? device.comment : '未設定'}
                        color={!device.comment ? '#bbb' : '#333'}
                      />
                    </Box>
                  </Box>
                  {isDeviceRegistered && (
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>最終通知日時</Typography>
                      <EllipsisTooltip value={(device.lastNotificationDate ? Utility.dateFormatYmdHis(device.lastNotificationDate) : '-') || '-'} />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* 端末状態 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1.2em', lg: '-10px' } }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>端末状態</Typography>
              {isDeviceRegistered ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography sx={style.subCategoryTitle}>状態情報</Typography>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px' }}>
                      <Typography sx={style.itemLabel}>電波強度</Typography>
                      <Box sx={{ ...style.itemValue, marginLeft: "-2.5px" }}>
                        {DeviceUiConverter.getSingignalLevelIcon(
                          device.signalLevel,
                          device.signalUpdateDate,
                          device.isOnline,
                          device.modelName,
                          device.networkType
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>メモリ使用率</Typography>
                      <EllipsisTooltip value={device.mem_use || '-'} />
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>CPU使用率</Typography>
                      <EllipsisTooltip value={device.cpu_use || '-'} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={style.subCategoryTitle}>設定情報</Typography>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px'}}>
                      <Typography sx={style.itemLabel}>WAN/LANポート設定</Typography>
                      <EllipsisTooltip value={device.wan?.mode ==="wan" ? "WAN" : device.wan?.mode ==="lan" ? "LAN" :  "-"} />
                    </Box>
                    <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                      <Typography sx={style.itemLabel}>自動アップロード設定</Typography>
                      <EllipsisTooltip value={device.autoupload === "0" ? "オフ" : "オン"} />
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  // marginTop: "-10px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* ネットワーク */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: '0' }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>ネットワーク</Typography>
              {isDeviceRegistered ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography sx={style.subCategoryTitle}>通信情報</Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px' }}>
                    <Typography sx={style.itemLabel}>メイン回線</Typography>
                    <EllipsisTooltip value={device.workmode || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>事業者</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        value={device.sim1?.operator && device.sim1?.operator.trim() !== '' ? device.carrier || device.sim1?.operator : '-'}
                        color={DeviceUiConverter.getCarrierColored(device.carrier || device.sim1?.operator, contentColor)}
                        isBold
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>IMSI</Typography>
                    <EllipsisTooltip value={device.sim1?.imsi || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>ICCID</Typography>
                    <EllipsisTooltip value={device.sim1?.iccid || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>モバイル通信状態</Typography>
                    <EllipsisTooltip value={device.sim1?.status || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>LANポート状態</Typography>
                    <EllipsisTooltip value={device.lan?.status || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>ネットワーク/モバイル</Typography>
                    <EllipsisTooltip value={`${device.nettype || '-'} / ${device.sim1?.nettype || '-'}`} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>CellID</Typography>
                    <EllipsisTooltip value={String(device.cellInfo?.cellId ?? '-')} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>LAC</Typography>
                    <EllipsisTooltip value={String(device.cellInfo?.lac ?? '-')} />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={style.subCategoryTitle}>アドレス情報</Typography>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em', marginTop: '-8px' }}>
                    <Typography sx={style.itemLabel}>モバイルIPアドレス</Typography>
                    <EllipsisTooltip value={device.sim1?.ip || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>モバイルIPv6アドレス</Typography>
                    <EllipsisTooltip value={device.sim1?.ipv6 || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>モバイル周波数</Typography>
                    <EllipsisTooltip value={device.sim1?.netband || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>WAN IPアドレス</Typography>
                    <EllipsisTooltip value={device.wan?.ip || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>WAN IPv6アドレス</Typography>
                    <EllipsisTooltip value={device.wan?.ipv6 || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>有線LAN IPアドレス</Typography>
                    <EllipsisTooltip value={device.lan?.ip || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>無線LANモード</Typography>
                    <EllipsisTooltip value={device.wlan?.mode || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>無線LAN IPアドレス</Typography>
                    <EllipsisTooltip value={device.wlan?.ip || '-'} />
                  </Box>
                  <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>無線LANチャネル</Typography>
                    <EllipsisTooltip value={device.wlan?.channel || '-'} />
                  </Box>
                  {/* <Box sx={{ ...style.itemBox, display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Typography sx={style.itemLabel}>無線LAN接続台数</Typography>
                    <EllipsisTooltip value={device.wlan?.number || '-'} />
                  </Box> */}
                </Grid>
              </Grid>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  marginTop: "-10px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* データ通信量 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1em', lg: '-9.2em' } }} elevation={0}>
            <Box sx={style.contentArea}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, height: "27px",}}>
                <Typography sx={{ ...style.categoryTitle }}>データ通信量</Typography>
                <Button
                  sx={{
                    fontSize: "0.8rem",
                    padding: "0 12px",
                    minWidth: "60px",
                    border: '1.5px solid #1976d2',
                    color: '#1976d2',
                    borderRadius: "18px",
                    background: "#f7fafd",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    transition: "background 0.2s, border 0.2s",
                    '&:hover': {
                      background: '#e3f2fd',
                    },
                  }}
                  onClick={() => {
                    sessionStorage.setItem("SIM_TYPE", "0");
                    navigate("/terminal_traffic_stats");
                  }}
                >
                  通信量グラフを表示
                  <ArrowForwardIosIcon sx={{ fontSize: "0.95rem", ml: 0.5 }} />
                </Button>
              </Box>
              {isDeviceRegistered ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: "#333", fontSize: '0.9em' }}>
                  <thead>
                    <tr style={{...style.subCategoryTitle, background: '#e7f2ff' }}>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' , minHeight: '28px', maxHeight: '28px', height: '28px'}}>種別</th>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>累積</th>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>1か月</th>
                        <th style={{padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>1日</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif", fontSize: '0.9em' }}>
                    <tr>
                      <td style={{padding: '0px 0px 2px 8px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px' }}>　SIM</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px'}}>{ByteFormatter.formatBytes({bytes: (Number(device.simTraffic) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.simTraffic1m) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                      <td style={{padding: '4px 12px', borderBottom: '1px solid #ccc'}}>{ByteFormatter.formatBytes({bytes: (Number(device.simTraffic1d) || 0) * 1024, unit:"MB", allowHigherUnits: true})}</td>
                    </tr>
                  </tbody>
                </table>
              </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  marginTop: "-10px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{
        flexDirection: { xs: 'column', lg: 'row' },
        '& > .MuiGrid-item': {
          maxWidth: { xs: 'none', lg: '50%' }
        }
      }}>
        {/* SIM差替えログ */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: '0' }} elevation={0}>
            <Box sx={{ ...style.contentArea }}>
              <Typography sx={style.categoryTitle}>SIM差替えログ</Typography>
              {isDeviceRegistered ? (
              <>
              <Grid container spacing={1} sx={{ marginBottom: "8px" }}>
                <Grid item xs={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: "0.75rem" }}>表示期間</InputLabel>
                    <Select
                      defaultValue={getValues("simReplacementLogDateStart")}
                      label="表示期間"
                      sx={{ fontSize: "0.75rem", height: "32px" }}
                      {...register("simReplacementLogDateStart")}
                    >
                      {simReplaceLogOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key} sx={{ fontSize: "0.75rem" }}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={1.5}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ height: "32px", fontSize: "0.8rem", padding: "0 12px", minWidth: "60px" }}
                  >
                    検索
                  </Button>
                </Grid>
              </Grid>
              <Box sx={{ overflowX: 'auto', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: '#e7f2ff'}}>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px' }}>No</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>差替え日時</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>タイプ</th>
                        <th style={{ padding: '4px 12px', fontWeight: 500, textAlign: 'left' }}>ICCID</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: "#464545ff", fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif" }}>
                    {Array.from({
                      length:
                        !device.simSwapLogs || device.simSwapLogs.length < 3
                          ? 3
                          : device.simSwapLogs.length === 10
                            ? 10
                            : device.simSwapLogs.length+1
                      }).map((_, index) => {
                      const log = device.simSwapLogs?.[index];
                      return (
                        <tr key={index}>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '50px' }}>
                            <TableCellTooltip value={log ? String(index + 1) : '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px' }}>
                            <TableCellTooltip value={log?.swapDate || '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '80px' }}>
                            {log?.simType ? (
                              <SimTypeBox
                                isBold
                                type={log.simType}
                                fontSize={contentFontSize}
                              />
                            ) : '-'}
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px' }}>
                            <TableCellTooltip value={log?.iccid || '-'} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              </>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  marginTop: "-10px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* 接続機器一覧 */}
        <Grid item lg={6}>
          <Paper sx={{ ...style.sectionBox, marginTop: { xs: '-1em', lg: '-27.7em'} }} elevation={0}>
            <Box sx={style.contentArea}>
              <Typography sx={style.categoryTitle}>接続機器一覧</Typography>
              {isDeviceRegistered ? (
              <Box sx={{ overflowX: 'auto', overflowY: 'auto', width: '100%'}}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8em', tableLayout: 'fixed'}}>
                  <thead>
                    <tr style={{...style.subCategoryTitle, background: '#e7f2ff', maxWidth: 440, flex: 1, minWidth: 0 }}>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left', minHeight: '28px', maxHeight: '28px', height: '28px' }}>No</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>接続機器名</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>MACアドレス</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>IPアドレス</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>接続形態</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>接続状態</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>接続時刻</th>
                        <th style={{ padding: '4px 7px', fontWeight: 500, textAlign: 'left' }}>PINGステータス</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: "#464545ff", fontWeight: 400, fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif" }}>
                    {Array.from({
                      length:
                        !device.equipments || device.equipments.length < 5
                          ? 5
                          : device.equipments.length === 15
                            ? 15
                            : device.equipments.length+1
                      }).map((_, index) => {
                      const equipment = device.equipments?.[index];
                      return (
                        <tr key={index} >
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', minHeight: '28px', maxHeight: '28px', height: '28px', maxWidth: '50px' }}>
                            <TableCellTooltip value={equipment ? String(index + 1) : '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '100px'}}>
                            <TableCellTooltip value={equipment?.name || '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px'}}>
                            <TableCellTooltip value={equipment?.mac || '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '100px'}}>
                            <TableCellTooltip value={equipment?.ip || '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '80px'}}>
                            <TableCellTooltip value={equipment?.connectionMode || equipment?.form || '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '100px'}}>
                            <TableCellTooltip value={equipment?.connectionStatus || '-'} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '120px'}}>
                            <TableCellTooltip value={(equipment?.connectionTime ? Utility.dateFormatYmdHi(equipment.connectionTime) : '-') as string} />
                          </td>
                          <td style={{ padding: '4px 12px', borderBottom: '1px solid #ccc', maxWidth: '100px' }}>
                            <TableCellTooltip value={equipment?.pingStatus || '-'} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '24px',
                  marginTop: "-10px",
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                      登録確認が行われていないため、表示できません。
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      ※該当端末がインターネット接続できる状態にしてください。
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DeviceDetailInfoGridForFS010M;